import React from 'react'
import { Box, Typography } from '@material-ui/core'
import ValidatedTextField from '../../../_global/ValidatedTextField'
import { validationSchema } from './validationSchema'
import { Form, Formik, FormikProps } from 'formik'
import { REFERRAL_TYPES } from '../../../shared/constants/paroReferrals'
import { useOnboarding } from '../../../contexts/OnboardingContext'
import { useMutation } from '@apollo/react-hooks'
import {
  createOrUpdateContactInfoMutation,
  saveUserPhoneNumberMutation,
} from '../../../apolloClient/mutations/createOrUpdateContactInfo'
import ValidatedDropdown from '../../../_global/ValidatedDropdown'
import CONTACT_INFO_QUERY from '../../../apolloClient/queries/getContactInfo'
import USER_INFO_QUERY from '../../../apolloClient//queries/getUserInfo'
import { useAuth0 } from '../../../contexts/Auth0Context'
import { useTextFieldStyles } from '../../../_global/OverrideStyles'
import FormActions from '../../../_global/FormActions'
import { populateLocationByZip, sanitizePhoneInput } from '../_local/service'
import { US_STATES } from '../../../shared/constants/usStates'

const ContactInfoForm = ({ initialValues }) => {
  const { logout } = useAuth0()
  const { step, next: nextStep, back, formRef } = useOnboarding()
  const [contactInformationMutation] = useMutation(createOrUpdateContactInfoMutation)
  const [saveUserPhoneNumber] = useMutation(saveUserPhoneNumberMutation)
  const classes = useTextFieldStyles()

  const {
    address1,
    address2,
    city,
    expertId,
    legacyFreelancerId,
    phone,
    region,
    paroReferrer,
    zipCode,
    referrerOther,
  } = initialValues

  const contactInfoValues = {
    address1: address1 || '',
    address2: address2 || '',
    zipCode: zipCode || '',
    region: region || '',
    city: city || '',
    phone: phone || '',
    paroReferrer: paroReferrer || '',
    referrerOther: referrerOther || '',
    isSaveAndExit: undefined,
  }

  const handleSubmit = async (values) => {
    const { address1, address2, phone, zipCode, city, region, paroReferrer, referrerOther, isSaveAndExit } = values
    const input = {
      address: {
        address1,
        address2,
        zipCode,
        city,
        region,
      },
      personalSummary: {
        paroReferrer: paroReferrer === '' ? null : paroReferrer,
        referrerOther,
      },
    }

    await contactInformationMutation({
      variables: { expertId, input },
      refetchQueries: [{ query: CONTACT_INFO_QUERY, variables: { id: expertId } }],
    })

    await saveUserPhoneNumber({
      variables: { id: legacyFreelancerId, phoneNumber: sanitizePhoneInput(phone) },
      refetchQueries: [{ query: USER_INFO_QUERY, variables: { id: legacyFreelancerId } }],
    }).then((result) => {
      if (isSaveAndExit) {
        logout()
      } else {
        if (step !== 7) {
          nextStep()
        }
      }
    })
  }

  return (
    <Formik
      validateOnMount={true}
      initialValues={contactInfoValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      innerRef={formRef}
    >
      {(props: FormikProps<any>) => {
        const { isSubmitting, isValid, values } = props
        return (
          <Form autoComplete="off">
            <ValidatedTextField
              label="Address Line 1"
              type="text"
              name="address1"
              style={{ width: '50%', marginRight: '2%' }}
            />

            <ValidatedTextField label="Address Line 2" name="address2" type="text" style={{ width: '40%' }} />
            <Box style={{ display: 'flex' }}>
              <ValidatedTextField
                label="Zip code"
                onChange={(e) => populateLocationByZip(e, props)}
                name="zipCode"
                type="text"
                style={{ width: '35%', marginRight: '2%' }}
              />
              
              <Box mt={2} style={{ width: '25%', marginRight: '2%' }}>
                <ValidatedDropdown
                  inputLabel="State"
                  label="State"
                  name="region"
                  items={US_STATES}
                  value={values.region || ''}
                />
              </Box>

              <ValidatedTextField
                className={classes.root}
                disabled={false}
                label="City"
                name="city"
                type="text"
                style={{ width: '35%' }}
              />
            </Box>

            <ValidatedTextField label="Phone Number" name="phone" type="text" style={{ width: '65%' }} />

            <Box mt={2}>
              <Typography variant="body2" gutterBottom>
                How did you hear about Paro?
              </Typography>
              <ValidatedDropdown
                name="paroReferrer"
                items={REFERRAL_TYPES}
                value={values.paroReferrer || ''}
                placeholder="Select"
              />

              {values.paroReferrer === 'OTHER' && (
                <ValidatedTextField label={'Please comment'} type="text" name="referrerOther" multiline />
              )}
            </Box>
            {step !== 7 && <FormActions step={step} back={back} isValid={isValid} isSubmitting={isSubmitting} />}
          </Form>
        )
      }}
    </Formik>
  )
}

export default ContactInfoForm
